import { getPriceGivenIntent } from '@src/components/Ladder/utils';
import type { PrimeOMSParams } from '@src/components/OMS/NewOrder/types';
import { getMultilegInstrumentType } from '@src/components/OMS/NewOrder/utils/mappers';
import { OMSView } from '@src/components/OMS/OMSView';
import { useGetOrderCurrency } from '@src/components/OMS/useGetOrderCurrency';
import { CardStatusIndicator } from '@src/components/StatusIndicator';
import { MarketDataStatusTable } from '@src/components/StatusTable';
import { SymbolSubhead } from '@src/components/SymbolDisplay/SymbolSubhead';
import type { SymbolDisplayProps } from '@src/components/SymbolDisplay/types';
import { TopOfBook, type TopOfBookProps } from '@src/components/TopOfBook';
import { useDefaultSubAccount, useMarketDataSnapshot } from '@src/hooks';
import { useIsPricingModeBPS } from '@src/hooks/useIsPricingModeBPS';
import { useMarketsContext, useSubAccounts } from '@src/providers';
import { useDisplaySettings, useTradingSettings } from '@src/providers/AppConfigProvider';
import { useAppStateDispatch } from '@src/providers/AppStateProvider';
import { useRfdDragIslandProps } from '@src/providers/RfdDragIslandContext';
import { getMarketDataLevelPrices } from '@src/utils/getMarketDataLevelPrice';
import {
  AggressorFlagEnum,
  Box,
  ConnectionType,
  EMPTY_ARRAY,
  Flex,
  FormControlSizes,
  HStack,
  IconButton,
  IconName,
  MarketDataCardSummary,
  MixpanelEvent,
  MixpanelEventProperty,
  MixpanelEventSource,
  OrdTypeEnum,
  OverridePopoverPropsProvider,
  Popover,
  MarketDataCardHeader as SharedMarketDataCardHeader,
  SideEnum,
  TOP_OF_BOOK_SIZE,
  UnifiedLiquidityEnum,
  VStack,
  isMultileg,
  isSecurityCME,
  isSpot,
  toBigWithDefault,
  useAggregationsContext,
  useCallbackRef,
  useMarketAccountsContext,
  useMixpanel,
  useObservable,
  useObservableRef,
  useObservableValue,
  usePopoverState,
  useSecuritiesContext,
  useUnifiedLiquidity,
  useUnifiedLiquidityAvailableSymbols,
  type ISubaccount,
  type OrderIntent,
  type Security,
} from '@talos/kyoko';
import { get } from 'lodash-es';
import { useCallback, useMemo } from 'react';
import { merge } from 'rxjs';
import { useCloneMultilegSymbol } from '../../hooks/useCloneMultilegSymbol';
import { useDeleteConfirmation } from '../../hooks/useDeleteConfirmation';
import { useIsMobileLayout } from '../../hooks/useIsMobileLayout';
import { useNavigateToDeepDive } from '../../hooks/useNavigateToDeepDive';
import { useNavigateToDeepDiveOrMultileg } from '../../hooks/useNavigateToDeepDiveOrMultileg';
import { useNavigateToMultilegCombo } from '../../hooks/useNavigateToMultilegCombo';
import { useOrderPresets, type IOrderPreset } from '../../providers/OrderPresetsContext';
import { DisclaimerTooltip, cmeDisclaimer } from '../DisclaimerTooltip';
import { getMarketSelectorAggregationLabel } from '../MarketSelector/presets/getMarketSelectorAggregationLabel';
import { useMarketSelectorAggregationPresets } from '../MarketSelector/presets/useMarketSelectorAggregationPresets';
import { UnifiedLiquidityTooltipIndicator } from '../MarketSelector/UnifiedLiquidityTooltipIndicator';
import { MultilegComboType } from '../MultilegCombo/enums';
import { rfqSliceActions } from '../OMS/NewRFQ/RFQSlice';
import { openView } from '../OMS/OMSSlice';
import { useOrderPresetSearchSelectOptions } from '../OrderPresets/OrderPresetSearchSelect';
import { useSubAccountSearchSelectOptions } from '../SubAccounts/useSubAccountSearchSelectOptions';
import { SymbolHeader } from '../SymbolDisplay/SymbolHeader';
import { ClickToTradeOrderPreview } from './ClickToTradeOrderPreview';
import { CardControlRow } from './components/CardControlRow';
import { MarketDataCardActionButtons } from './components/MarketDataCardActionButtons';
import { MarketCardDisplayLabels } from './MarketCardDisplayLabels';
import type { MultilegMarketAccountTooltipGroup } from './MarketCardMarketAccountButton';
import { getUnifiedLiquidityMarketTooltipGroups } from './marketCardUnifiedLiquidityTooltip';
import { useMarketDataCardMarketSelectorContext } from './MarketDataCardMarketSelectorContext';
import { MarketDataCardMenu } from './MarketDataCardMenu';
import { MarketDataCardSymbolSelector } from './MarketDataCardSymbolSelector';
import { getMultilegUnifiedLiquidityMarketAccounts } from './multilegLegMarketAccounts';
import { AVA_MARKET_DATA_CARD_MDS_THROTTLE, type MarketDataCardHeaderProps } from './types';
import { useMarketDataCard } from './useMarketDataCard';

const TOP_OF_BOOK_STYLE = { padding: '0 1rem' };
const DISABLE_POPOVER_TRIGGER = { trigger: '' as const };

export const MarketDataCardHeader = ({
  config,
  security,
  activeOrder,
  isDraggable,
  defaultSubAccountId,
  onClickPrice,
  onCancelActive,
  onClickSettings,
  onSaveCard,
  onDelete,
  onOpenMarketSelector,
  marketSelectorTourDataTestId,
  onOpenClearCustomRate,
  onOpenPublishCustomRate,
  canClearCustomRate,
}: MarketDataCardHeaderProps) => {
  const rfdDragIslandProps = useRfdDragIslandProps();
  const {
    id,
    selectedMarketAccounts,
    orderPresetID,
    selectedMarkets,
    unifiedLiquidityMarketSymbols,
    symbol,
    topOfBookSizeBucket,
    title,
    unifiedLiquidity,
    alwaysDisplayBestBidOffer,
    subAccount,
  } = config;

  const { useNewMarketSelector, enableDefaultMarketCardSubAccount } = useTradingSettings();
  const isInMobileMode = useIsMobileLayout();
  const { DefaultPriceIncrement } = security || {};

  const { aggregationsByName } = useAggregationsContext();
  const { aggregationPresetsByID } = useMarketSelectorAggregationPresets();
  const { subAccountsByName } = useSubAccounts();
  const subAccountOptions = useSubAccountSearchSelectOptions();
  const quickMarketSelectorContext = useMarketDataCardMarketSelectorContext();
  const selectedMarketOrAccountNames = useMemo(
    () => quickMarketSelectorContext?.selectedMarketOrAccountNames ?? [...selectedMarkets, ...selectedMarketAccounts],
    [quickMarketSelectorContext?.selectedMarketOrAccountNames, selectedMarketAccounts, selectedMarkets]
  );
  const selectedAggregationID = quickMarketSelectorContext?.selectedAggregationID ?? config.selectedAggregation;
  const resolvedAggregationLabel = getMarketSelectorAggregationLabel(
    selectedAggregationID,
    aggregationPresetsByID,
    aggregationsByName
  );
  const favoriteAggregationPresets = quickMarketSelectorContext?.favoriteAggregationPresets ?? [];
  const selectAggregationPreset = quickMarketSelectorContext?.selectAggregationPreset;

  const mixpanel = useMixpanel();

  const dispatch = useAppStateDispatch();

  const { showAllInPrices } = useDisplaySettings();

  const isPricingModeBPS = useIsPricingModeBPS(security);

  const { canSubmitOrder, canViewOrder, canRequestRFQ } = useMarketDataCard(security);

  const { isMarketSupported, marketDisplayNameByName } = useMarketsContext();
  const { marketAccountDisplayNameByName, marketAccountsByName } = useMarketAccountsContext();
  const { securitiesBySymbol } = useSecuritiesContext();
  const { getOrderCurrency } = useGetOrderCurrency();

  const securityMarkets = security?.Markets || [];
  const isRfqSupported = isSpot(security) && securityMarkets.some(m => isMarketSupported(m, ConnectionType.RFQ));

  const unifiedLiquidityToken = useUnifiedLiquidity(symbol);
  const effectiveUnifiedLiquidityMarketSymbols = unifiedLiquidityMarketSymbols ?? EMPTY_ARRAY;

  const availableSymbols = useUnifiedLiquidityAvailableSymbols({
    marketAccounts: selectedMarketAccounts,
    markets: selectedMarkets,
    symbol: symbol,
  });

  // Based on Unified Liquidity, creates list of underlying quote currencies including security symbol.
  const quoteCurrencies = useMemo(() => {
    if (unifiedLiquidity !== UnifiedLiquidityEnum.Enabled || !unifiedLiquidityToken || !security) {
      return [];
    }

    const quoteCurrenciesSet = new Set<string>();

    for (const availableSymbol of availableSymbols) {
      const availableSecurity = securitiesBySymbol.get(availableSymbol);
      if (availableSecurity) {
        quoteCurrenciesSet.add(availableSecurity.QuoteCurrency);
      }
    }

    return Array.from(quoteCurrenciesSet);
  }, [unifiedLiquidity, unifiedLiquidityToken, security, availableSymbols, securitiesBySymbol]);

  const subscriptionAddress = `${id}/${TOP_OF_BOOK_SIZE}`;
  // We don't need to subscribe if we're always showing the best bid offer
  const { marketDataSnapshots, tradeDirections, marketDataErrors } = useMarketDataSnapshot(
    alwaysDisplayBestBidOffer
      ? null
      : {
          marketAccounts: selectedMarketAccounts,
          markets: selectedMarkets,
          unifiedLiquidityMarketSymbols: effectiveUnifiedLiquidityMarketSymbols,
          priceIncrement: DefaultPriceIncrement,
          symbol,
          quantity: topOfBookSizeBucket,
          tag: subscriptionAddress,
          includeTradeDirection: true,
          currency: undefined,
          depth: undefined,
          sizeBuckets: undefined,
          unifiedLiquidity,
          throttle: AVA_MARKET_DATA_CARD_MDS_THROTTLE,
        }
  );
  const marketDataSnapshot = useObservableRef(() => marketDataSnapshots, [marketDataSnapshots]);

  const bboSubscriptionAddress = `${id}/BBO`;

  const bid: string | undefined = get(marketDataSnapshot.current, `Bids[0].Price`);
  const offer: string | undefined = get(marketDataSnapshot.current, `Offers[0].Price`);

  // Wait until we have a snapshot to determine if we should use the BBO snapshot
  // We still need to keep the original subscription going if liquidity improves
  const useBBOSnapshot =
    alwaysDisplayBestBidOffer ||
    (marketDataSnapshot.current && toBigWithDefault(bid, 0).eq(0) && toBigWithDefault(offer, 0).eq(0));

  // Best Bid Offer snapshot for Depth = 1
  const bboSnapshot = useMarketDataSnapshot(
    useBBOSnapshot
      ? {
          markets: selectedMarkets,
          marketAccounts: selectedMarketAccounts,
          unifiedLiquidityMarketSymbols: effectiveUnifiedLiquidityMarketSymbols,
          priceIncrement: DefaultPriceIncrement,
          symbol,
          currency: undefined,
          tag: bboSubscriptionAddress,
          includeTradeDirection: true,
          depth: 1,
          unifiedLiquidity,
          throttle: AVA_MARKET_DATA_CARD_MDS_THROTTLE,
        }
      : null
  );

  const currentMarketDataSnapshot = useObservableRef(() => {
    return useBBOSnapshot && bboSnapshot?.marketDataSnapshots ? bboSnapshot.marketDataSnapshots : marketDataSnapshots;
  }, [useBBOSnapshot, marketDataSnapshots, bboSnapshot.marketDataSnapshots]);

  /**
   * Since either one of these merged observables will be active, we merge them and use it for showing statuses.
   */
  const statusCarryingMarketDataSnapshots = useObservable(
    () => merge(bboSnapshot.marketDataSnapshots, marketDataSnapshots),
    [bboSnapshot.marketDataSnapshots, marketDataSnapshots]
  );
  const statusCarryingMarketDataSnapshot = useObservableValue(
    () => statusCarryingMarketDataSnapshots,
    [statusCarryingMarketDataSnapshots]
  );
  const statusCarryingMarketDataErrors = useObservableValue(
    () => merge(bboSnapshot.marketDataErrors, marketDataErrors),
    [bboSnapshot.marketDataErrors, marketDataErrors]
  );

  const handleTopOfBookSizeUpdate = useCallback<NonNullable<TopOfBookProps['onUpdateSize']>>(
    (index, newSize) => {
      mixpanel.track(MixpanelEvent.ChangeTopOfBookSize, { [MixpanelEventProperty.Size]: newSize });
      onSaveCard({ ...config, topOfBookSizeBucket: newSize });
    },
    [config, mixpanel, onSaveCard]
  );

  const handleTopOfBookClickPrice = useCallback<NonNullable<TopOfBookProps['onClickPrice']>>(
    values =>
      onClickPrice({
        // @ts-expect-error - values are inconsistent between the parameter and the source
        values,
        source: MixpanelEventSource.TopOfBook,
        unifiedLiquidityMarketSymbols: effectiveUnifiedLiquidityMarketSymbols,
      }),
    [effectiveUnifiedLiquidityMarketSymbols, onClickPrice]
  );

  const popover = usePopoverState({
    usePortal: false,
    trigger: 'hover',
    placement: 'bottom',
    isSmall: true,
    delay: undefined,
  });

  const handleClickOnBid = () => primeOmsWithSnapshot({ side: SideEnum.Buy, intent: AggressorFlagEnum.Passive });

  const handleClickOnOffer = () => primeOmsWithSnapshot({ side: SideEnum.Sell, intent: AggressorFlagEnum.Passive });

  const { defaultSubAccount } = useDefaultSubAccount(defaultSubAccountId);

  const handleClickOnRfq = () => {
    mixpanel.track(MixpanelEvent.OpenRfqForm);
    if (!security) {
      return;
    }
    const params: PrimeOMSParams = {
      ordType: OrdTypeEnum.LimitAllIn,
      group: '',
      symbol: security.Symbol,
      currency: security.BaseCurrency,
      orderQty: topOfBookSizeBucket,
      marketAccounts: selectedMarketAccounts,
      subAccount: subAccount || defaultSubAccount?.Name,
    };
    dispatch(openView(OMSView.RFQForm));
    dispatch(rfqSliceActions.primeNewRFQForm(params));
  };

  const primeOmsWithSnapshot = ({ side, intent }: { side: SideEnum; intent: OrderIntent }) => {
    const snapshot = currentMarketDataSnapshot.current;
    if (snapshot == null) {
      return;
    }
    const { bidPrice, offerPrice } = getMarketDataLevelPrices({ snapshot, index: 0, isPricingModeBPS });
    const price = getPriceGivenIntent(side, intent, bidPrice ?? '', offerPrice ?? '');
    const currency = getOrderCurrency(security, side);
    onClickPrice({
      values: {
        side,
        currency,
        orderQty: topOfBookSizeBucket,
        price,
        intent,
      },
      source: MixpanelEventSource.BidOfferButtons,
      unifiedLiquidityMarketSymbols: effectiveUnifiedLiquidityMarketSymbols,
      overrideSide: true,
      activateOrder: false,
    });
  };

  // Order presets
  const { orderPresetsByID, orderPresetsList } = useOrderPresets();
  const handleChangeOrderPreset = useCallbackRef((preset?: IOrderPreset) => {
    mixpanel.track(MixpanelEvent.SelectOrderPreset, { source: MixpanelEventSource.MarketCardHeader });
    onSaveCard({ ...config, orderPresetID: preset?.id });
  });
  const orderPresetSelection = orderPresetID == null ? undefined : orderPresetsByID.get(orderPresetID);
  const orderPresetOptions = useOrderPresetSearchSelectOptions({ security });
  const { updateOrderPreset } = useOrderPresets();
  const handleToggleFavoritePreset = useCallbackRef((orderPreset: IOrderPreset) => {
    updateOrderPreset(orderPreset.id, {
      ...orderPreset,
      isFavorite: !orderPreset.isFavorite,
    });
  });

  // SubAccounts
  const handleSubAccountChange = useCallbackRef((subAccount?: ISubaccount) => {
    mixpanel.track(MixpanelEvent.SelectSubAccount, { source: MixpanelEventSource.MarketCardHeader });
    onSaveCard({ ...config, subAccount: subAccount?.Name });
  });
  const subAccountSelection = subAccount ? subAccountsByName.get(subAccount) : undefined;
  const handleChangeAggregation = useCallbackRef((aggregationID: string) => {
    selectAggregationPreset?.(aggregationID);
  });

  // Market Data Card Menu handlers
  const navigateToDeepDive = useNavigateToDeepDive();
  const navigateToDeepDiveOrMultileg = useNavigateToDeepDiveOrMultileg();
  const navigateToMultilegCombo = useNavigateToMultilegCombo();
  const { confirmDelete } = useDeleteConfirmation();

  const handleOpenDeepDive = useCallbackRef(() => {
    navigateToDeepDiveOrMultileg(security, selectedMarketAccounts, selectedMarkets, undefined);
  });

  const handleOpenMobileDeepDive = useCallbackRef(() => {
    if (!security || isMultileg(security)) {
      return;
    }

    navigateToDeepDive(security, selectedMarketAccounts, selectedMarkets, selectedAggregationID);
  });

  const handleDeleteCard = useCallbackRef(() => {
    confirmDelete({
      title: 'Delete Market Data Card',
      itemName: `the card for ${symbol}`,
      onConfirm: () => {
        mixpanel.track(MixpanelEvent.DeleteCard, {
          [MixpanelEventProperty.Source]: MixpanelEventSource.MarketCardHeader,
        });
        onDelete(config);
      },
    });
  });

  const handleCreateMultileg = useCallbackRef((multilegType: MultilegComboType) => {
    if (!security) {
      return;
    }
    mixpanel.track(MixpanelEvent.CreateNewMultileg, {
      [MixpanelEventProperty.Type]: multilegType,
      [MixpanelEventProperty.Source]: MixpanelEventSource.MarketCardHeader,
    });
    navigateToMultilegCombo({
      instrumentType: multilegType,
      marketAccountNames: selectedMarketAccounts,
      marketNames: selectedMarkets,
      legSymbols: [security.Symbol],
    });
  });

  const handleEditSymbol = useCallbackRef(() => {
    if (!security || !isMultileg(security)) {
      return;
    }
    mixpanel.track(MixpanelEvent.EditMultileg, {
      [MixpanelEventProperty.Source]: MixpanelEventSource.MarketCardHeader,
    });
    navigateToMultilegCombo({
      instrumentType:
        getMultilegInstrumentType(security.MultilegDetails?.SyntheticProductType) ?? MultilegComboType.SyntheticCross,
      security,
      isEdit: true,
    });
  });

  const cloneMultilegSymbol = useCloneMultilegSymbol(MixpanelEventSource.MarketCardHeader);
  const isMultilegSecurity = isMultileg(security);
  const handleOpenMarketSelector = useCallbackRef((event: MixpanelEvent) => {
    if (!onOpenMarketSelector) {
      return;
    }
    mixpanel.track(event, {
      [MixpanelEventProperty.Source]: MixpanelEventSource.MarketCardHeader,
      [MixpanelEventProperty.IsMultileg]: isMultilegSecurity,
    });
    onOpenMarketSelector();
  });

  // If we are provided onOpenMarketSelector, then always open the market selector.
  // Else, if we are multileg, we're showing an Edit button to let the user go edit the entire multileg instrument.
  const marketSelectorButtonOnClick = onOpenMarketSelector
    ? () => handleOpenMarketSelector(MixpanelEvent.OpenMarketSelectorFromMarketCardButton)
    : isMultilegSecurity
      ? handleEditSymbol
      : undefined;

  const marketSelectorMenuOnClick = onOpenMarketSelector
    ? () => handleOpenMarketSelector(MixpanelEvent.OpenMarketSelectorFromMarketCardMenu)
    : undefined;

  const marketName =
    selectedMarketAccounts.length > 0
      ? marketAccountDisplayNameByName.get(selectedMarketAccounts[0])
      : marketDisplayNameByName.get(selectedMarkets[0]);

  const showOrderPresets = orderPresetsList.length > 0;
  const showSubAccountSelector = enableDefaultMarketCardSubAccount && subAccountOptions.length > 0;
  const marketSelectorButtonLabel =
    isMultilegSecurity && !onOpenMarketSelector
      ? 'Edit Symbol'
      : selectedAggregationID
        ? (resolvedAggregationLabel ?? selectedAggregationID)
        : 'Custom';
  const showNoMarketSelectionWarning =
    !isMultilegSecurity && selectedMarketOrAccountNames.length === 0 && selectedAggregationID == null;
  const showSmallControls = showOrderPresets || showSubAccountSelector || marketSelectorButtonOnClick != null;
  const unifiedLiquidityTooltipGroups = useMemo(
    () =>
      unifiedLiquidity === UnifiedLiquidityEnum.Enabled && unifiedLiquidityToken
        ? getUnifiedLiquidityMarketTooltipGroups({
            primarySymbol: symbol,
            primarySecurity: security,
            unifiedLiquidityMarketSymbols: effectiveUnifiedLiquidityMarketSymbols ?? [],
            selectedMarkets,
            selectedMarketAccounts,
            unifiedLiquidityTokens: unifiedLiquidityToken.Tokens,
            marketAccountsByName,
            marketDisplayNameByName,
            securitiesBySymbol,
            snapshotMarkets: statusCarryingMarketDataSnapshot?.Markets,
          })
        : [],
    [
      effectiveUnifiedLiquidityMarketSymbols,
      marketAccountsByName,
      marketDisplayNameByName,
      securitiesBySymbol,
      selectedMarketAccounts,
      selectedMarkets,
      statusCarryingMarketDataSnapshot?.Markets,
      security,
      symbol,
      unifiedLiquidity,
      unifiedLiquidityToken,
    ]
  );
  const multilegMarketAccountTooltipGroups = useMemo(
    () =>
      isMultilegSecurity
        ? getMultilegMarketAccountTooltipGroups({
            security,
            selectedMarketAccounts,
            includeUnifiedLiquidity: unifiedLiquidity === UnifiedLiquidityEnum.Enabled,
            securitiesBySymbol,
          })
        : [],
    [isMultilegSecurity, securitiesBySymbol, security, selectedMarketAccounts, unifiedLiquidity]
  );
  const actionButtonSize = showSmallControls ? FormControlSizes.Xxs : FormControlSizes.Small;
  const actionItems = canViewOrder ? (
    <VStack w="100%" alignItems="stretch" px="spacing16" gap="spacing4" flex="1">
      <div {...(rfdDragIslandProps ?? {})}>
        <CardControlRow
          showMarketSelectorNavigation={!isMultilegSecurity || onOpenMarketSelector != null}
          isEditSymbolMode={isMultilegSecurity && onOpenMarketSelector == null}
          showOrderPresets={showOrderPresets}
          showSubAccountSelector={showSubAccountSelector}
          onMarketSelectorClick={marketSelectorButtonOnClick}
          aggregationPresets={favoriteAggregationPresets}
          selectedAggregationID={selectedAggregationID}
          onChangeAggregation={handleChangeAggregation}
          marketSelectorButtonLabel={marketSelectorButtonLabel}
          marketSelectorTourDataTestId={marketSelectorTourDataTestId}
          selectedMarketOrAccountNames={selectedMarketOrAccountNames}
          unifiedLiquidityTooltipGroups={unifiedLiquidityTooltipGroups}
          multilegMarketAccountTooltipGroups={multilegMarketAccountTooltipGroups}
          orderPresetSelection={orderPresetSelection}
          onChangeOrderPreset={handleChangeOrderPreset}
          onToggleFavoritePreset={handleToggleFavoritePreset}
          orderPresetOptions={orderPresetOptions}
          subAccountSelection={subAccountSelection}
          subAccountOptions={subAccountOptions}
          onSubAccountChange={handleSubAccountChange}
        />
      </div>
      <MarketDataCardActionButtons
        canSubmitOrder={canSubmitOrder}
        canRequestRFQ={canRequestRFQ}
        isRfqSupported={isRfqSupported}
        size={actionButtonSize}
        onClickBid={handleClickOnBid}
        onClickRfq={handleClickOnRfq}
        onClickOffer={handleClickOnOffer}
      />
      {!showSmallControls ? <div style={{ height: '8px' }} /> : null}
    </VStack>
  ) : null;

  const topLeftItems = <MarketCardDisplayLabels security={security} />;

  const topRightItems = (
    <HStack m="-spacing4" data-testid="top-right-items">
      {isSecurityCME(security) && <DisclaimerTooltip>{cmeDisclaimer}</DisclaimerTooltip>}
      {selectedMarkets.length || selectedMarketAccounts.length ? (
        <span data-testid="market-status-popover">
          <Popover {...popover}>
            <CardStatusIndicator
              selectedMarkets={selectedMarkets}
              selectedMarketAccounts={selectedMarketAccounts}
              snapshots={statusCarryingMarketDataSnapshots}
              websocketError={statusCarryingMarketDataErrors}
            />
            <MarketDataStatusTable
              snapshots={statusCarryingMarketDataSnapshots}
              websocketError={statusCarryingMarketDataErrors}
            />
          </Popover>
        </span>
      ) : (
        <CardStatusIndicator
          selectedMarkets={selectedMarkets}
          selectedMarketAccounts={selectedMarketAccounts}
          snapshots={statusCarryingMarketDataSnapshots}
          websocketError={statusCarryingMarketDataErrors}
        />
      )}
      {isInMobileMode ? (
        <Box m="-spacing4">
          <IconButton
            icon={IconName.Cog}
            onClick={onClickSettings}
            ghost
            data-testid="market-card-mobile-settings-button"
          />
        </Box>
      ) : (
        <MarketDataCardMenu
          security={security}
          onOpenSettings={onClickSettings}
          onOpenDeepDive={handleOpenDeepDive}
          onDeleteCard={handleDeleteCard}
          onCreateMultileg={isInMobileMode ? undefined : handleCreateMultileg}
          onEditSymbol={isMultilegSecurity ? handleEditSymbol : undefined}
          onCloneSymbol={cloneMultilegSymbol}
          onOpenMarketSelector={marketSelectorMenuOnClick}
          onOpenClearCustomRate={onOpenClearCustomRate}
          onOpenPublishCustomRate={onOpenPublishCustomRate}
          canClearCustomRate={canClearCustomRate}
        />
      )}
    </HStack>
  );

  const symbolHeaderProps: SymbolDisplayProps = {
    security,
    title,
    quoteCurrencies,
    unifiedLiquidity,
    unifiedLiquidityIndicator: unifiedLiquidityTooltipGroups.length ? (
      <UnifiedLiquidityTooltipIndicator
        baseSymbol={symbol}
        groups={unifiedLiquidityTooltipGroups}
        dataTestId="market-card-header-unified-liquidity-tooltip"
        statusTestIdPrefix="market-card-header-unified-liquidity-tooltip-status"
      />
    ) : undefined,
    availableSymbols,
    multipleSelections: selectedMarketAccounts.length + selectedMarkets.length > 1,
    selectedAggregation:
      config.selectedAggregation && useNewMarketSelector
        ? aggregationsByName?.get(config.selectedAggregation)
        : undefined,
    selectedMarketAccounts,
    marketName,
    tooltipOnOverflow: !isInMobileMode,
    showNoMarketSelectionWarning,
    onNoMarketSelectionWarningClick: showNoMarketSelectionWarning ? marketSelectorButtonOnClick : undefined,
  };

  return (
    <>
      <SharedMarketDataCardHeader
        actionItems={actionItems}
        isDraggable={isDraggable}
        security={security}
        slotFlexRule="1"
        topCenterItems={
          isInMobileMode ? (
            <OverridePopoverPropsProvider overrideProps={DISABLE_POPOVER_TRIGGER}>
              <Box background="none" border="none" as="button" type="button" onClick={handleOpenMobileDeepDive}>
                <SymbolHeader {...symbolHeaderProps} />
              </Box>
            </OverridePopoverPropsProvider>
          ) : (
            <MarketDataCardSymbolSelector
              config={config}
              symbol={symbol}
              symbolHeaderProps={symbolHeaderProps}
              onSaveCard={onSaveCard}
              onOpenMarketSelectorFromHeaderButton={() =>
                handleOpenMarketSelector(MixpanelEvent.OpenMarketSelectorFromMarketCardButton)
              }
            />
          )
        }
        topSubheader={<SymbolSubhead {...symbolHeaderProps} />}
        topLeftItems={topLeftItems}
        topRightItems={topRightItems}
      >
        <TopOfBook
          style={TOP_OF_BOOK_STYLE}
          security={security}
          topOfBookSizeBucket={topOfBookSizeBucket}
          topOfBookSnapshots={marketDataSnapshots}
          alwaysShowBestBidOffer={alwaysDisplayBestBidOffer}
          tradeDirections={tradeDirections}
          showSideLabels={canViewOrder ? 'hover' : 'hidden'}
          activeOrder={activeOrder}
          isReadOnly={!canViewOrder}
          showAllInPrices={!!showAllInPrices}
          onClickPrice={handleTopOfBookClickPrice}
          onMouseLeavePrice={onCancelActive}
          onUpdateSize={handleTopOfBookSizeUpdate}
          bboSnapshots={bboSnapshot.marketDataSnapshots}
          isPricingModeBPS={isPricingModeBPS}
        />
      </SharedMarketDataCardHeader>

      {activeOrder || actionItems ? (
        <Flex alignItems="stretch" justifyContent="center" w="100%">
          {activeOrder && security ? (
            <MarketDataCardSummary>
              <ClickToTradeOrderPreview clickToTradePreviewOrder={activeOrder} security={security} />
            </MarketDataCardSummary>
          ) : (
            actionItems
          )}
        </Flex>
      ) : null}
    </>
  );
};

function getMultilegMarketAccountTooltipGroups({
  security,
  selectedMarketAccounts,
  includeUnifiedLiquidity,
  securitiesBySymbol,
}: {
  security: Security | undefined;
  selectedMarketAccounts: string[];
  includeUnifiedLiquidity: boolean;
  securitiesBySymbol: Map<string, Security>;
}): MultilegMarketAccountTooltipGroup[] {
  const legGroups =
    security?.MultilegDetails?.Legs?.map((leg, index) => ({
      key: `leg-${index}`,
      displayName: `${leg.Symbol ? (securitiesBySymbol.get(leg.Symbol)?.DisplaySymbol ?? leg.Symbol) : 'Leg'} (Leg ${index + 1})`,
      marketAccountNames: leg.Markets?.map(market => market.MarketAccount) ?? [],
    })) ?? [];

  if (includeUnifiedLiquidity) {
    legGroups.push({
      key: 'ul',
      displayName: 'Unified Liquidity',
      marketAccountNames: getMultilegUnifiedLiquidityMarketAccounts(selectedMarketAccounts), // ul leg market account selections are stored in this array
    });
  }

  return legGroups;
}
